import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material'
import getDisabledChoices from 'lib/getCarDisabledChoices'
import T from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { mapLocation } from 'lib/carDetailsLib'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import HeartDownloadIcon from 'assets/images/icons/heart-download'

import CarCard from 'components/atoms/CarCard'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarsList from 'components/atoms/CarsList'
import CommentLink from 'components/atoms/CommentLink'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import PagePartnersSelect from 'components/atoms/PagePartnersSelect'
import PageTitle from 'components/atoms/PageTitle'
import SearchField from 'components/atoms/SearchField'

import useCarsFilters from 'hooks/useCarsFilters'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useExport from 'hooks/useExport'
import useInputOutput from 'hooks/useInputOutput'

import CarCardChoices from './CarCardChoices'
import CarBlindPrice from './CarBlindPrice'
import CarReprice from './CarReprice'
import CarsToBuyDetails from './CarsToBuyDetails'
import CarsToBuyPageTitleRightColumn from './CarsToBuyPageTitleRightColumn'
import CarsToBuySessionExpired from './CarsToBuySessionExpired'
import ChoicesRecap from './ChoicesRecap'
import MessageDialog from './MessageDialog'
import { getCarsToBuyUrl } from './carsToBuyLib'

const mapCars = ({
  admin,
  blindOffersRemaining,
  disabledChoices,
  filteredCars,
  handleConfirmReprice,
  handleConfirmBlindPrice,
  getSelection,
  onSelectionChange,
  partnerId,
  readOnly,
  blindReadOnly,
  selectedCarsToBuy,
  showBlindPrice,
  showBlindOption,
  showNonBlindReprice,
  showPriceRange,
  session,
  setShowDetails,
  onCommentClick,
  contentIsReadOnly,
  onToggleFavorite,
  showFavorites,
}) =>
  filteredCars.map((car, i) => (
    <CarCard
      data-testid={`car-${i}`}
      key={car.originalId}
      onClick={() => setShowDetails(car.plate)}
      height={showNonBlindReprice || showBlindPrice ? 225 : 209}
      showPriceRange={showPriceRange}
      car={car}
      price={car.price}
      maxPrice={car.maxPrice}
      onToggleFavorite={
        showFavorites
          ? () => onToggleFavorite(car.plate, car.favorite)
          : undefined
      }
      right={
        <CarCardChoices
          session={session}
          partnerId={partnerId}
          car={car}
          disabledChoices={disabledChoices}
          blindOffersRemaining={blindOffersRemaining}
          selectedChoices={
            session.showOtherPartners
              ? selectedCarsToBuy?.find(
                  (selectedCar) => selectedCar.plate === car.plate
                )
              : undefined
          }
          readOnly={admin || readOnly || contentIsReadOnly}
          selection={getSelection(car)}
          onSelectionChange={onSelectionChange}
          showBlindOption={showBlindOption}
        />
      }
      bottom={
        <Stack direction="row">
          <Stack
            mt={2}
            mr={2}
            justifyContent="center"
            sx={{ flex: '1 0 auto' }}
          >
            <CommentLink
              handleClick={() => onCommentClick(car.plate)}
              commentsNumber={car?.messages?.length}
              contentIsReadOnly={contentIsReadOnly}
              advisory={car?.publicAdvice}
            />
          </Stack>
          {showNonBlindReprice || showBlindPrice ? (
            <Stack direction="column" spacing={1} sx={{ width: 1 }}>
              {showNonBlindReprice && (
                <CarReprice
                  car={car}
                  handleConfirmReprice={handleConfirmReprice}
                  handleShowDetails={() => setShowDetails(car.plate)}
                  readOnly={readOnly}
                  sx={{ width: 1 }}
                />
              )}
              {showBlindPrice && (
                <>
                  {(admin
                    ? car.blindPrice !== null &&
                      car.blindPrice !== undefined &&
                      car.blindPrice !== ''
                      ? true
                      : Number(car.blindOfferCount || 0) > 0
                    : true) && (
                    <CarBlindPrice
                      car={car}
                      handleConfirmBlindPrice={handleConfirmBlindPrice}
                      handleShowDetails={() => setShowDetails(car.plate)}
                      readOnly={admin || blindReadOnly}
                      showConfirmButton={!admin}
                      enabled={getSelection(car) === 'blind'}
                      blindOffersRemaining={blindOffersRemaining}
                      sx={{ width: 1 }}
                    />
                  )}
                </>
              )}
            </Stack>
          ) : undefined}
        </Stack>
      }
    />
  ))

function getRowsForDownload(titlesRow, cars, t) {
  return [
    titlesRow,
    ...cars.map((car) => [
      `${car.title}`,
      car.km,
      mapLocation(car.location),
      t(`carDetails.mapping.${car.transmission}`),
      car.reconditioningEstCost || '-',
      car.year,
      car.fuelType,
      car.plate,
      Math.round(car.optionAmount) || '-',
      car.firstChoice
        ? '1'
        : car.blindPrice !== null && car.blindPrice !== undefined
        ? t('carsToBuy.choice.blind')
        : car.secondChoice
        ? '2'
        : '-',
      car.favorite
        ? t('common.filters.boolean.true')
        : t('common.filters.boolean.false'),
    ]),
  ]
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function CarsToBuy({
  session,
  previousSession,
  admin,
  partnerId,
  partners,
  updateChoice = () => {},
  setReprice = () => {},
  addMessage,
  toggleFavorite = () => {},
  cars,
  selectedCarsToBuy,
  countDown,
  readOnly,
  contentIsReadOnly = false,
  showFavorites = false,
  isBlindPublication = false,
  showHeader = true,
  showPartnerSelect = true,
  title,
  stateKey,
  showBlindPrice = false,
  blindOffersRemaining,
  blindReadOnly = false,
  setBlindPrice = () => {},
  blindSessionId,
  blindCaps,
}) {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])
  const [selectedPlate, setSelectedPlate] = useState(null)
  const [blindSelectionByPlate, setBlindSelectionByPlate] = useState({})

  const { saveXls } = useInputOutput()
  const { saveExport, isFetching: isFetchingExport } = useExport()

  const caps = useMemo(
    () =>
      (isBlindPublication ? session.partnerBlindCaps : session.partnerCaps) ||
      {},
    [isBlindPublication, session]
  )

  const resolvedBlindOffersRemaining = useMemo(() => {
    if (!isBlindPublication) {
      return blindOffersRemaining ?? null
    }

    const blindCap = Number(caps?.firstChoice ?? 0)
    const usedOffers = cars.filter(
      (car) => car.blindPrice !== null && car.blindPrice !== undefined
    ).length
    const remaining = blindCap - usedOffers

    return Number.isFinite(remaining) ? Math.max(remaining, 0) : null
  }, [blindOffersRemaining, caps, cars, isBlindPublication])

  const resolvedBlindCaps = useMemo(() => {
    if (isBlindPublication) {
      return caps
    }
    return blindCaps || {}
  }, [blindCaps, caps, isBlindPublication])

  const blindOffersTotal = useMemo(() => {
    const total = Number(resolvedBlindCaps?.firstChoice ?? 0)
    return Number.isFinite(total) ? total : 0
  }, [resolvedBlindCaps])

  const blindOffersUsed = useMemo(() => {
    if (
      resolvedBlindOffersRemaining !== null &&
      resolvedBlindOffersRemaining !== undefined
    ) {
      return Math.max(blindOffersTotal - resolvedBlindOffersRemaining, 0)
    }
    return cars.filter(
      (car) => car.blindPrice !== null && car.blindPrice !== undefined
    ).length
  }, [blindOffersTotal, resolvedBlindOffersRemaining, cars])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    setCaps: setFilteringCaps,
    setOnlyFavorites,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(addMessage)

  useEffect(() => {
    if (!cars.length || filters?.initialized) {
      return
    }

    initFilters(cars)
  }, [cars, filters?.initialized, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const disabledChoices = useMemo(
    () => getDisabledChoices(cars, caps),
    [caps, cars]
  )

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const handleSetFilteringCaps = (value) => {
    setFilteringCaps(value)
    if (filters.onlyFavorites) {
      setOnlyFavorites(false)
    }
  }

  const handleToggleFavoritesFilter = () => {
    const newValue = !filters.onlyFavorites
    setOnlyFavorites(newValue)
    // If turning on favorites filter, reset the caps filter
    if (newValue && filters.caps) {
      setFilteringCaps('')
    }
  }

  const handleConfirmReprice = useCallback(
    (plate, reprice) => {
      setReprice(session.sessionId, partnerId, plate, reprice)
    },
    [partnerId, session, setReprice]
  )

  const showNonBlindReprice = !!session.canReprice && !isBlindPublication
  const blindPriceSessionId =
    blindSessionId || (isBlindPublication ? session.sessionId : null)
  const resolvedShowBlindOption = isBlindPublication || showBlindPrice
  const resolvedShowBlindPrice =
    !admin && resolvedShowBlindOption && !!blindPriceSessionId
  const showBlindOption = resolvedShowBlindOption

  const getSelection = useCallback(
    (car) => {
      if (car.firstChoice) {
        return 'selected'
      }
      if (car.blindPrice !== null && car.blindPrice !== undefined) {
        return 'blind'
      }
      return blindSelectionByPlate[car.plate] ? 'blind' : 'none'
    },
    [blindSelectionByPlate]
  )

  const clearBlindSelection = useCallback((plate) => {
    setBlindSelectionByPlate((current) => {
      if (!current[plate]) {
        return current
      }
      const next = { ...current }
      delete next[plate]
      return next
    })
  }, [])

  const handleSelectionChange = useCallback(
    async (car, nextSelection) => {
      if (readOnly) {
        return false
      }

      if (nextSelection === 'blind') {
        const hasRemainingBlindOffers =
          resolvedBlindOffersRemaining === null ||
          resolvedBlindOffersRemaining === undefined ||
          Number(resolvedBlindOffersRemaining) > 0
        const hasExistingBlindPrice =
          car.blindPrice !== null &&
          car.blindPrice !== undefined &&
          car.blindPrice !== ''

        if (!hasRemainingBlindOffers && !hasExistingBlindPrice) {
          return false
        }

        if (car.firstChoice) {
          const success = await updateChoice(
            session.sessionId,
            partnerId,
            car.plate,
            'none'
          )
          if (!success) {
            return false
          }
        }
        setBlindSelectionByPlate((current) => ({
          ...current,
          [car.plate]: true,
        }))
        return true
      }

      const choice = nextSelection === 'selected' ? 'first' : 'none'
      const success = await updateChoice(
        session.sessionId,
        partnerId,
        car.plate,
        choice
      )
      if (!success) {
        return false
      }

      clearBlindSelection(car.plate)
      if (
        resolvedShowBlindPrice &&
        car.blindPrice !== null &&
        car.blindPrice !== undefined
      ) {
        await setBlindPrice(blindPriceSessionId, partnerId, car.plate, null)
      }

      return true
    },
    [
      readOnly,
      updateChoice,
      session.sessionId,
      partnerId,
      resolvedBlindOffersRemaining,
      clearBlindSelection,
      resolvedShowBlindPrice,
      setBlindPrice,
      blindPriceSessionId,
    ]
  )

  const handleConfirmBlindPrice = useCallback(
    async (plate, reprice) => {
      if (!blindPriceSessionId) {
        return
      }
      await setBlindPrice(blindPriceSessionId, partnerId, plate, reprice)
      if (reprice === null || reprice === undefined || reprice === '') {
        clearBlindSelection(plate)
      }
    },
    [blindPriceSessionId, partnerId, setBlindPrice, clearBlindSelection]
  )

  const onCommentClick = useCallback(
    (plate) => {
      setSelectedPlate(plate)
      handleOpenConfirmationDialog()
    },
    [handleOpenConfirmationDialog]
  )

  const onToggleFavorite = useCallback(
    (plate, currentFavoriteState) => {
      toggleFavorite(session.sessionId, partnerId, plate, currentFavoriteState)
    },
    [toggleFavorite, session.sessionId, partnerId]
  )

  const handleResetPlateOnCloseConfirmationDialog = useCallback(() => {
    setSelectedPlate(null)
    handleCloseConfirmationDialog()
  }, [handleCloseConfirmationDialog])

  const carsContent = useMemo(
    () =>
      mapCars({
        admin,
        blindOffersRemaining: resolvedBlindOffersRemaining,
        disabledChoices,
        filteredCars,
        handleConfirmReprice,
        handleConfirmBlindPrice,
        getSelection,
        onSelectionChange: handleSelectionChange,
        partnerId,
        readOnly,
        blindReadOnly,
        selectedCarsToBuy,
        showBlindPrice: resolvedShowBlindPrice,
        showBlindOption,
        showNonBlindReprice,
        session,
        setShowDetails,
        onCommentClick,
        contentIsReadOnly,
        onToggleFavorite,
        showFavorites,
        showPriceRange: !isBlindPublication && !resolvedShowBlindPrice,
      }),
    [
      admin,
      disabledChoices,
      filteredCars,
      handleConfirmReprice,
      handleConfirmBlindPrice,
      getSelection,
      handleSelectionChange,
      resolvedBlindOffersRemaining,
      partnerId,
      readOnly,
      blindReadOnly,
      selectedCarsToBuy,
      session,
      onCommentClick,
      contentIsReadOnly,
      onToggleFavorite,
      showFavorites,
      resolvedShowBlindPrice,
      showBlindOption,
      showNonBlindReprice,
      isBlindPublication,
    ]
  )

  const carFilters = useMemo(
    () => (
      <CarFiltersColumn
        filters={filters}
        filtersData={filtersData}
        filteredCars={
          filteredCars?.length !== cars.length ? filteredCars : undefined
        }
        setKmRange={setKmRange}
        setDamageRange={setDamageRange}
        setYearRange={setYearRange}
        setBrands={setBrands}
        setModels={setModels}
        setLocations={setLocations}
        setFuels={setFuels}
        setCategory={setCategory}
        resetFilters={resetFilters}
        readOnly={readOnly}
      />
    ),
    [
      filters,
      filtersData,
      filteredCars,
      cars.length,
      setKmRange,
      setDamageRange,
      setYearRange,
      setBrands,
      setModels,
      setLocations,
      setFuels,
      setCategory,
      resetFilters,
      readOnly,
    ]
  )

  const handlePartnerChange = (event) => {
    navigate(getCarsToBuyUrl(event.target.value, null, isBlindPublication))
  }

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      t('carDetails.optionals'),
      t('common.excel.choice'),
      t('common.excel.favorite'),
    ]

    const rows = getRowsForDownload(titlesRow, cars, t)

    saveXls(`cars-to-buy`, rows)
  }

  const handleDownloadFavorites = async () => {
    const favoriteCars = cars.filter((car) => car.favorite)

    if (favoriteCars.length === 0) {
      return
    }

    const titlesRow = [
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      t('carDetails.optionals'),
      t('common.excel.choice'),
      t('common.excel.favorite'),
    ]

    const rows = getRowsForDownload(titlesRow, favoriteCars, t)

    saveXls(`cars-to-buy-favorites`, rows)
  }

  const handleAdditionalFileDownload = async () => {
    await saveExport({
      name: `dati_veicoli_${session.sessionId}`,
      params: { partnerId, sessionId: session.sessionId },
      path: 'carsToBuy',
    })
  }

  const resolvedStateKey =
    stateKey || (isBlindPublication ? 'blindPublication' : 'publication')

  return showDetails ? (
    <CarsToBuyDetails
      partnerId={partnerId}
      plate={showDetails}
      cars={filteredCars}
      allCars={cars}
      session={session}
      isBlindPublication={isBlindPublication}
      setReprice={setReprice}
      setBlindPrice={setBlindPrice}
      blindSessionId={blindSessionId}
      showBlindPrice={resolvedShowBlindPrice}
      blindReadOnly={blindReadOnly}
      blindOffersRemaining={resolvedBlindOffersRemaining}
      toggleFavorite={showFavorites ? toggleFavorite : undefined}
      selectedCarsToBuy={selectedCarsToBuy}
      setShowDetails={setShowDetails}
      updateChoice={updateChoice}
    />
  ) : (
    <>
      {showHeader && (
        <PageTitle
          label={title || t('carsToBuy.title')}
          rightColumn={
            <>
              <CarsToBuyPageTitleRightColumn
                countDown={countDown}
                stateKey={resolvedStateKey}
              />
              {showPartnerSelect && partners.length > 0 && (
                <>
                  <Box pr={2} />
                  <PagePartnersSelect
                    partners={partners}
                    value={partnerId}
                    handleChange={handlePartnerChange}
                    readOnly={readOnly}
                  />
                </>
              )}
            </>
          }
        />
      )}
      {!session?.sessionId || (countDown.initialized && countDown.expired) ? (
        <CarsToBuySessionExpired
          session={session}
          previousSession={previousSession}
        />
      ) : (
        <>
          <ConfirmBar
            label={t('carsToBuy.confirmButton')}
            left={
              <SearchField
                placeholder={t('common.form.searchBy')}
                value={filters.query}
                onChange={handleChangeSearch}
                data-testid="text-search"
              />
            }
            leftProps={{ sx: { width: 0.5 } }}
            right={
              <>
                <DownloadBar handleDownload={handleDownload} />
                {showFavorites && (
                  <Tooltip title={t('carsToBuy.downloadFavorites')}>
                    <IconButton
                      data-testid="download-favorites"
                      aria-label={t('carsToBuy.downloadFavorites')}
                      onClick={handleDownloadFavorites}
                      disabled={!cars.some((car) => car.favorite)}
                      sx={{ ml: 0.5 }}
                    >
                      <HeartDownloadIcon
                        style={{ fontSize: 20, color: 'black' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Collapse orientation="horizontal" in={isFetchingExport}>
                  <div style={{ marginLeft: 4, marginRight: 4 }}>
                    <CircularProgress size={20} />
                  </div>
                </Collapse>
                <IconButton
                  data-testid="download-additional-file"
                  aria-label={t('carsToBuy.downloadAdditionalFile')}
                  onClick={handleAdditionalFileDownload}
                  disabled={isFetchingExport}
                >
                  <FolderOutlinedIcon style={{ color: 'black' }} />
                </IconButton>
              </>
            }
            top={
              <ChoicesRecap
                cars={cars}
                caps={caps}
                blindOffersUsed={blindOffersUsed}
                blindOffersTotal={blindOffersTotal}
                showBlindOffers={showBlindOption}
                filteringCap={filters.caps}
                setFilteringCaps={handleSetFilteringCaps}
                onlyFavorites={
                  showFavorites ? filters.onlyFavorites : undefined
                }
                onToggleFavorites={
                  showFavorites ? handleToggleFavoritesFilter : undefined
                }
              />
            }
            topProps={{
              sx: {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              },
            }}
            hideConfirm
            sticky
          />
          <CarsList left={carFilters} right={carsContent} />

          <MessageDialog
            messages={cars.find((car) => car.plate === selectedPlate)?.messages}
            openConfirmationDialog={openConfirmationDialog}
            handleCloseConfirmationDialog={
              handleResetPlateOnCloseConfirmationDialog
            }
            handleConfirmConfirmationDialog={(message) =>
              handleConfirmConfirmationDialog(
                session?.sessionId,
                partnerId,
                selectedPlate,
                message
              )
            }
            readOnly={admin}
          />
        </>
      )}
    </>
  )
}

CarsToBuy.propTypes = {
  session: T.object.isRequired,
  previousSession: T.object,
  admin: T.bool,
  partnerId: T.string,
  partners: T.array,
  updateChoice: T.func,
  setReprice: T.func,
  addMessage: T.func,
  toggleFavorite: T.func,
  cars: T.array,
  selectedCarsToBuy: T.array,
  countDown: T.object,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
  showFavorites: T.bool,
  isBlindPublication: T.bool,
  showHeader: T.bool,
  showPartnerSelect: T.bool,
  title: T.string,
  stateKey: T.string,
  showBlindPrice: T.bool,
  blindOffersRemaining: T.number,
  blindReadOnly: T.bool,
  setBlindPrice: T.func,
  blindSessionId: T.string,
  blindCaps: T.object,
}

export default CarsToBuy
